// Para onde leva o botão "Abrir a tela" de um alerta da Vick.
//
// O destino é decidido AQUI, no front, e não pelo `link` que o vigia grava.
// Dois motivos:
//
//   1. O vigia não conhece as rotas do app. Ele gravou `/crm/inadimplencia`
//      por analogia — rota que nunca existiu: a Central de Inadimplência é um
//      MODAL aberto por evento global, não uma página. O clique caía num CRM
//      em branco. Foi exatamente o "me jogou pra lugar nenhum" relatado.
//   2. Alerta já gravado carrega o link antigo para sempre. Corrigir aqui
//      vale para a fila inteira; corrigir no vigia só valeria para os
//      próximos.
//
// `alerta.link` continua como fallback: detector novo que o front ainda não
// conhece pelo menos abre o módulo certo em vez de nada.
//
// ⚠️ Destino sem `ondeResolver` não deveria existir. Abrir a tela certa e
// largar a pessoa numa página densa é metade do problema — o Simulador de
// Rateio tem seis abas e nenhuma delas se chama "saldo". A frase é o que
// transforma "abriu" em "resolveu".

import type { IaAlerta } from '../../types';

export interface DestinoAlerta {
  /** Rótulo do botão: diz o destino, não "abrir a tela". */
  rotulo: string;
  /** Rota do react-router. Ausente quando o destino é um modal. */
  rota?: string;
  /** Modal aberto por CustomEvent global (padrão da Central de Inadimplência). */
  evento?: { nome: string; detalhe?: unknown };
  /** O que fazer depois de chegar lá. */
  ondeResolver: string;
}

const texto = (v: unknown): string | null => {
  if (v == null) return null;
  const s = String(v).trim();
  return s.length > 0 ? s : null;
};

/**
 * O `site_id_api` da usina, que o detector guardou na chave (`site|falha`).
 * É o mesmo valor do seletor de usina da aba Análise.
 */
const siteDaChave = (chave: string): string | null => texto(chave.split('|')[0]);

export function destinoDoAlerta(alerta: IaAlerta): DestinoAlerta | null {
  const d = alerta.dados ?? {};

  switch (alerta.tipo) {
    case 'saldo_baixo':
    case 'saldo_excedente':
      return {
        rotulo: 'Abrir Rateio › Simulador',
        rota: '/gestao/rateio',
        ondeResolver:
          'Escolha a UG e o mês de referência: a cobertura de cada UC aparece na coluna ' +
          '"Saldo Acumulado" da grade, e o resumo em âmbar no topo repete a contagem. ' +
          'A decisão é caso a caso — nada é ajustado sozinho.',
      };

    case 'reincluir_no_rateio':
      return {
        rotulo: 'Abrir Gestão › Saldos das UCs',
        rota: '/gestao/rateio-saldos?filtro=reincluir',
        ondeResolver:
          'A lista já abre só com as UCs fora do rateio com saldo acabando, a de menos saldo primeiro. ' +
          'Em cada uma, "Ajustar" registra o pedido "Incluir no rateio" (já preenchido); quando a planilha ' +
          'seguinte trouxer a UC recebendo, o painel confirma sozinho. ' +
          (Number(d.pausadas_no_cadastro) > 0
            ? `${d.pausadas_no_cadastro} estão "Pausada" no cadastro: um gestor abre a UC e usa o "Destravar" antes de a planilha subir.`
            : ''),
      };

    case 'planilha_rateio_atrasada':
      return {
        rotulo: 'Abrir Gestão › Rateio Antigo',
        rota: '/gestao/rateio-antigo',
        ondeResolver:
          `Suba a planilha de ${texto(d.planilha_esperada) ?? 'rateio do mês anterior'} pelo botão de upload. ` +
          `Até lá, a Janela de Faturamento de ${texto(d.mes_das_faturas) ?? 'este mês'} repete a de ` +
          `${texto(d.planilha_repetida) ?? 'último mês carregado'}; quando a nova subir, ela troca sozinha e o alerta fecha.`,
      };

    case 'variacao_consumo':
      return {
        rotulo: 'Abrir Rateio › Simulador',
        rota: '/gestao/rateio',
        ondeResolver:
          `Escolha a UG e o mês ${texto(d.mes) ?? 'do alerta'}: a coluna de consumo mostra o ` +
          'desvio contra a média cadastrada. Corrigir a média muda o rateio do mês seguinte.',
      };

    case 'usina_parada': {
      const site = siteDaChave(alerta.chave);
      return {
        rotulo: 'Abrir Geradoras › Análise',
        rota: site
          ? `/geradoras?aba=analise&site=${encodeURIComponent(site)}`
          : '/geradoras?aba=analise',
        ondeResolver:
          d.falha === 'sem_leitura' || d.falha === 'nunca_reportou'
            ? 'A usina já vem selecionada. Falha de COLETA: a curva estará truncada na última ' +
              'leitura — é chamado de Tecnologia (integração), não de campo.'
            : 'A usina já vem selecionada. Falha de GERAÇÃO: a leitura chega, mas zerada — ' +
              'é campo, alguém precisa ir até a usina.',
      };
    }

    case 'inadimplencia_acumulando':
      return {
        rotulo: 'Abrir Central de Inadimplência',
        evento: { nome: 'vigorhub:open-inadimplencia', detalhe: { tab: 'ranking' } },
        ondeResolver:
          'Abre na aba "Maiores Devedores". Cada linha abre a janela de gerenciamento de ' +
          'cobrança do cliente (analista, gerente ou adm).',
      };

    case 'boleto_nao_emitido': {
      const uc = texto(d.uc);
      return {
        rotulo: uc ? `Abrir Faturas › UC ${uc}` : 'Abrir Financeiro › Faturas',
        rota: uc ? `/financeiro/faturas?busca=${encodeURIComponent(uc)}` : '/financeiro/faturas',
        ondeResolver: uc
          ? `A lista já abre filtrada pela UC ${uc}. Ache o mês ${texto(d.mes) ?? 'do alerta'} e ` +
            'emita a cobrança pelo menu da linha.'
          : 'Busque pela UC e emita a cobrança pelo menu da linha.',
      };
    }

    case 'boleto_sem_codigo_barras': {
      const uc = texto(d.uc);
      // O caminho depende de a cobrança poder existir no Sicoob: com nosso número,
      // cancelar antes é obrigatório, senão a reemissão vira cobrança duplicada.
      const comNossoNumero = d.tem_nosso_numero === true;
      const passos = comNossoNumero
        ? 'Cancele o boleto pelo menu da linha ANTES de emitir de novo — pode existir cobrança registrada no banco. ' +
          'O cancelamento baixa o título se ele existir.'
        : 'Cancele o boleto pelo menu da linha e emita de novo — nenhuma cobrança foi criada, não há risco de duplicar.';
      return {
        rotulo: uc ? `Abrir Faturas › UC ${uc}` : 'Abrir Financeiro › Faturas',
        rota: uc ? `/financeiro/faturas?busca=${encodeURIComponent(uc)}` : '/financeiro/faturas',
        ondeResolver: (uc
          ? `A lista já abre filtrada pela UC ${uc}. Ache o mês ${texto(d.mes) ?? 'do alerta'}. `
          : 'Busque pela UC e ache o mês do alerta. ') + passos +
          (d.ja_enviado_ao_cliente === true
            ? ' O boleto já foi enviado ao cliente: avise que o anterior não era pagável.'
            : ''),
      };
    }

    case 'boleto_orfao': {
      const uc = texto(d.uc);
      // A resolução NÃO é no VigorHub: o boleto órfão existe no internet banking e
      // some do sistema por construção (o campo foi sobrescrito). O que a tela da
      // fatura oferece — cancelar — só alcança o boleto VIVO, que aqui é o certo.
      const numeros = Array.isArray(d.boletos_sem_cancelamento)
        ? d.boletos_sem_cancelamento.map((n: unknown) => String(n)).join(', ')
        : null;
      return {
        rotulo: uc ? `Abrir Faturas › UC ${uc}` : 'Abrir Financeiro › Faturas',
        rota: uc ? `/financeiro/faturas?busca=${encodeURIComponent(uc)}` : '/financeiro/faturas',
        ondeResolver: d.gateway === 'parceira'
          ? `Consulte ${numeros ? `a(s) cobrança(s) ${numeros}` : 'as cobranças anteriores'} ` +
            'no painel do Asaas. Se estiverem em aberto, cancele-as por lá: elas não existem mais ' +
            `no VigorHub. A cobrança correta desta fatura é a ${texto(d.boleto_vivo_no_sistema) ?? 'que está na tela'} — não cancele essa.`
          : `Consulte ${numeros ? `o(s) nosso(s) número(s) ${numeros}` : 'os boletos anteriores'} ` +
            'no internet banking do Sicoob (Cobrança › Consultas › Boleto). Se estiverem em aberto, ' +
            'baixe-os por lá: eles não existem mais no VigorHub e o cliente pode pagá-los por DDA. ' +
            `O boleto correto desta fatura é o ${texto(d.boleto_vivo_no_sistema) ?? 'que está na tela'} — não cancele esse.`,
      };
    }

    case 'queda_recebimento':
      return {
        rotulo: 'Abrir Financeiro › Painel Executivo',
        rota: '/financeiro/painel-executivo',
        ondeResolver:
          'A evolução da inadimplência e o aging estão lá. Mês recente ainda está sendo ' +
          'pago — a taxa sobe com o tempo, então compare de novo depois.',
      };

    default:
      // Detector que o front ainda não conhece: o link do vigia é melhor que nada.
      return alerta.link
        ? { rotulo: 'Abrir a tela', rota: alerta.link, ondeResolver: '' }
        : null;
  }
}
